// src/components/PublisherNav.jsx
// Phase P1: Publisher Navigation Component
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Building2, User, LogOut, ChevronDown, Settings, BarChart3 } from 'lucide-react';
import RoleSwitcher from './RoleSwitcher';

const PublisherNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getDisplayName = () => {
    if (user?.first_name) return user.first_name;
    if (user?.name) return user.name.split(' ')[0];
    return 'Publisher';
  };

  return (
    <nav className="sticky top-0 z-50 border-b border-slate-800 bg-slate-950/95 backdrop-blur">
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/publisher" className="flex items-center gap-3">
            <img
              src="/newaudia-logo.jpg"
              alt="NewAudia"
              className="h-10 w-auto rounded-lg"
            />
            <span className="hidden sm:inline text-xs text-blue-400 font-medium bg-blue-900/30 px-2 py-0.5 rounded-full">Publisher</span>
          </Link>

          {/* Right side - Role Switcher + User Menu */}
          <div className="flex items-center gap-3">
            {/* Role Switcher for multi-role users */}
            <RoleSwitcher />
            
            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center gap-2 rounded-full bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700 transition-colors"
              >
                <div className="h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center text-xs font-bold text-white">
                  {getDisplayName().charAt(0).toUpperCase()}
                </div>
                <span className="hidden sm:inline">{getDisplayName()}</span>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </button>

              {showDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowDropdown(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-slate-700 bg-slate-900 py-2 shadow-xl z-20">
                    <div className="px-4 py-2 border-b border-slate-700">
                      <p className="text-sm font-medium text-slate-200">{user?.name || 'Publisher'}</p>
                      <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                    </div>
                    
                    <Link
                      to="/publisher"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800"
                      onClick={() => setShowDropdown(false)}
                    >
                      <Building2 className="h-4 w-4" />
                      Publisher Dashboard
                    </Link>
                    
                    <Link
                      to="/publisher/profile"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800"
                      onClick={() => setShowDropdown(false)}
                    >
                      <User className="h-4 w-4" />
                      Publisher Profile
                    </Link>
                    
                    <Link
                      to="/publisher/analytics"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800"
                      onClick={() => setShowDropdown(false)}
                    >
                      <BarChart3 className="h-4 w-4" />
                      Analytics
                    </Link>
                    
                    <Link
                      to="/account"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800"
                      onClick={() => setShowDropdown(false)}
                    >
                      <Settings className="h-4 w-4" />
                      Account Settings
                    </Link>
                    
                    <div className="border-t border-slate-700 mt-2 pt-2">
                      <button
                        onClick={() => {
                          setShowDropdown(false);
                          handleLogout();
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-slate-800"
                      >
                        <LogOut className="h-4 w-4" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default PublisherNav;
